import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Grid,
    Typography,
    Stack,
    Theme,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@apollo/client';
import { NetCard, NetCardLoading } from './NetCard';
import { NetsDocument } from '@/graphql/generated/fetch-net-types.generated';
import { Net } from '@/graphql/generated/types.generated';
import { useTranslation } from 'react-i18next';
import { visibleNets } from '@/utils/profiles/visible-nets';
import { getProfilesPath, managesProfilesInBrands } from '@/utils/profiles/profiles-path';
import { useSession } from '@/providers/session-provider';

export interface ConnectNetDialogProps {
    open: boolean;
    /**
     * Show only the networks that can be published to.
     *
     * Prefer this over computing `filterNets` in the caller: this component already runs the `nets`
     * query, so a caller running a second one purely to build a filter is asking the same question
     * twice. It is also the safer default to reach for, because the query's own default is
     * `onlyPublish: false` - every network the API knows, including Pinterest, Tumblr, Blogger,
     * Wordpress and Meta Ads, several of which have no icon and render as a grey letter tile.
     */
    onlyPublish?: boolean;
    /**
     * Restrict to an explicit set of net codes, for cuts the query cannot express. The analytics
     * surfaces use this: they are limited to the networks that have a dash source, which is a
     * client-side constant rather than a flag on the query.
     */
    filterNets?: string[] | undefined;
    /**
     * Called only when an attempt actually added or reconnected a profile, and the dialog closes with
     * it. A failed or cancelled attempt leaves the dialog open and captures `profile.connect.failed`.
     */
    onConnect?: () => void;
    onClose?: () => void;
    /** The brand that receives the new profile. Without it, a brand pricing workspace is sent to its brands page. */
    brandId?: number;
}

export default function ConnectNetDialog({
    open,
    onlyPublish = false,
    filterNets,
    onConnect,
    onClose,
    brandId,
}: ConnectNetDialogProps) {
    const { t } = useTranslation();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const { workspace } = useSession();
    // Each brand holds one profile per network and the plan limit is the number of brands, so a new
    // profile gets its slot on the brands page. Skipping the query leaves the grid below empty.
    const needsBrand = brandId == null && managesProfilesInBrands(workspace);
    const { data, loading } = useQuery(NetsDocument, {
        variables: { onlyPublish },
        notifyOnNetworkStatusChange: true,
        skip: needsBrand,
    });

    return (
        <Dialog open={open} fullWidth maxWidth="md" fullScreen={isMobile}>
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5">{t('str.connect-profiles')}</Typography>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label={t('str.close', 'Close')}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {needsBrand && (
                    <Stack alignItems="center" gap={2} py={3} textAlign="center">
                        <Typography>{t('social-profile.connect-dialog.brands-hint')}</Typography>
                        {/* A new tab keeps whatever the user was editing here (a post, a widget). */}
                        <Button
                            variant="contained"
                            href={getProfilesPath(workspace)}
                            target="_blank"
                            rel="noopener"
                            onClick={onClose}
                            data-id="connect-net-dialog-go-to-brands"
                        >
                            {t('social-profile.connect-dialog.go-to-brands')}
                        </Button>
                    </Stack>
                )}
                <Grid container spacing={2}>
                    {loading ? (
                        <>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }} key="loading-1">
                                <NetCardLoading />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }} key="loading-2">
                                <NetCardLoading />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }} key="loading-3">
                                <NetCardLoading />
                            </Grid>
                        </>
                    ) : (
                        visibleNets(data?.nets)
                            .filter((net) => !filterNets || filterNets?.includes(net.code))
                            .map((net: Net) => (
                                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={net.id}>
                                    <Stack justifyContent="center" alignItems="center">
                                        <NetCard
                                            net={net}
                                            brandId={brandId}
                                            surface="connect-dialog"
                                            onConnect={() => {
                                                onConnect?.();
                                                onClose?.();
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                            ))
                    )}
                </Grid>
            </DialogContent>
        </Dialog>
    );
}
